import React, {useState} from 'react'
import {
    Box,
    Img,
    Text,
    Flex,
    useStyleConfig,
    useMediaQuery,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import {remCalc} from '../../../theme/_utils'
import PropTypes from 'prop-types'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {ChevronRight} from '../../icons'

const defaultColors = {
    blue: '#E1E1E3',
    grey: '#E2DCD5',
    red: '#EFE0DD',
    blue_text: '#001787',
    grey_text: '#39342D',
    red_text: '#440707',
}

const MenuSimpleCard = ({
    card,
    categoriesData,
    locale,
    isLarge = false,
    showSubtitle = true,
    showChevron = true,
}) => {
    const styles = useStyleConfig('PlpTopCards', {})
    const [isHovered, setIsHovered] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [isMobileOrTablet] = useMediaQuery('(max-width: 1199px)')
    const shouldShowButton = isHovered || isFocused || isMobileOrTablet
    const normalizedCard = card?.card ?? card
    const colorKey = (normalizedCard?.card_color || 'blue').toLowerCase()
    const bgColor = defaultColors[colorKey] ? colorKey : 'blue'
    const textColor = defaultColors[`${colorKey}_text`] ? `${colorKey}_text` : 'blue_text'
    const cardIcon = normalizedCard?.icon ?? null
    const {label, url, isAbsolute} =
        getGlobalLink({
            link: normalizedCard?.redirect,
            categoriesData: categoriesData,
            locale: locale?.alias,
        }) || {}

    return (
        <>
            <Box
                as={url ? (isAbsolute ? 'a' : RouteLink) : undefined}
                href={url && isAbsolute ? url : undefined}
                to={url && !isAbsolute ? url : undefined}
                target={url && isAbsolute ? '_blank' : undefined}
                rel={url && isAbsolute ? 'noopener noreferrer' : undefined}
                aria-label={
                    url
                        ? normalizedCard?.title_text ||
                          label ||
                          normalizedCard?.subtitle_text ||
                          'Open link'
                        : undefined
                }
                backgroundColor={defaultColors[bgColor]}
                width="100%"
                minW="0"
                minHeight={isLarge ? '100%' : remCalc(120)}
                height={isLarge ? '100%' : remCalc(120)}
                position="relative"
                display="block"
                padding="xsmall-1.base"
                borderRadius="large-1"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                textDecoration="none"
            >
                {cardIcon && (
                    <Img
                        src={cardIcon?.url}
                        color={textColor}
                        width={{
                            base: remCalc(20),
                            sm: remCalc(20),
                            md: 'iconsSize.medium',
                            lg: 'iconsSize.medium',
                        }}
                        height={{
                            base: remCalc(20),
                            sm: remCalc(20),
                            md: 'iconsSize.medium',
                            lg: 'iconsSize.medium',
                        }}
                        position="absolute"
                        left="xsmall-1.base"
                        top="xsmall-1.base"
                    />
                )}
                <Flex
                    gap="xsmall.base"
                    position="absolute"
                    bottom="xsmall-1.base"
                    left="xsmall-1.base"
                    right="xsmall-1.base"
                    alignItems="flex-end"
                    justifyContent="space-between"
                    minW="0"
                >
                    <Flex flexDir="column" gap={remCalc(4)} minW="0">
                        {(normalizedCard?.title_text || label) && (
                            <Text
                                textStyle="body-medium"
                                fontWeight="medium"
                                color={defaultColors[textColor]}
                                whiteSpace="normal"
                                wordBreak="break-word"
                                maxW="100%"
                            >
                                {normalizedCard?.title_text || label}
                            </Text>
                        )}
                        {showSubtitle && normalizedCard?.subtitle_text && (
                            <Text
                                textStyle="body-medium"
                                fontWeight="regular"
                                color={defaultColors[textColor]}
                                whiteSpace="normal"
                                wordBreak="break-word"
                                maxW="100%"
                            >
                                {normalizedCard?.subtitle_text}
                            </Text>
                        )}
                    </Flex>
                    {showChevron && url && (
                        <Flex
                            {...styles?.buttonContainer}
                            opacity={shouldShowButton ? 0.5 : 0}
                            transition="opacity 0.2s ease"
                            pointerEvents={shouldShowButton ? 'auto' : 'none'}
                            flexShrink={0}
                        >
                            <ChevronRight {...styles?.chevronIcon} />
                        </Flex>
                    )}
                </Flex>
            </Box>
        </>
    )
}

MenuSimpleCard.propTypes = {
    card: PropTypes.object,
    categoriesData: PropTypes.object,
    locale: PropTypes.object,
    isLarge: PropTypes.bool,
    showSubtitle: PropTypes.bool,
    showChevron: PropTypes.bool,
}

export default MenuSimpleCard
